import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { StoreProvider } from "@/lib/store";
import { PlaybackProvider } from "@/lib/playback";
import { ThemeProvider } from "@/components/theme-provider";
import { TourProvider } from "@/components/tour/TourProvider";
import { TourOverlay } from "@/components/tour/TourOverlay";
import { Analytics } from "@vercel/analytics/react";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import MusicLeagueTransfer from "./pages/MusicLeagueTransfer";
import Dashboard from "./pages/Dashboard";
import CreateLeague from "./pages/CreateLeague";
import JoinLeague from "./pages/JoinLeague";
import Leagues from "./pages/Leagues";
import Profile from "./pages/Profile";
import LeagueDetail from "./pages/LeagueDetail";
import SubmitSong from "./pages/SubmitSong";
import VoteRound from "./pages/VoteRound";
import RoundResults from "./pages/RoundResults";
import NotFound from "./pages/NotFound";
import Playlists from "./pages/Playlists";
import PlaylistPlayerPage from "./pages/PlaylistPlayer";
import Admin from "./pages/Admin";
import Features from "./pages/Features";
import CreateSeason from "./pages/CreateSeason";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import LeagueView from "./pages/LeagueView";
import SeasonView from "./pages/SeasonView";
import DbDebug from "./pages/DbDebug";

import { useStore } from "@/lib/store";

const RequireAuth = ({ children }: { children: JSX.Element }) => {
  const { state, isLoading } = useStore();
  
  // Wait for loading to complete before checking authentication
  if (isLoading) {
    // Show a loading state while checking authentication
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!state.currentUser) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

const queryClient = new QueryClient();

const App = () => (
  <StoreProvider>
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem disableTransitionOnChange>
      <PlaybackProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <TourProvider>
                <TourOverlay />
                <Routes>
                <Route path="/" element={<Landing />} />
                {/* League-specific routes - MORE SPECIFIC ROUTES FIRST */}
                <Route path="/:leagueSlug/seasons/:seasonId" element={<SeasonView />} />
                <Route path="/:leagueSlug/leagues" element={<Leagues />} />
                <Route path="/:leagueSlug/admin" element={
                  <RequireAuth>
                    <Admin />
                  </RequireAuth>
                } />
                <Route path="/:leagueSlug/debug-db" element={<DbDebug />} />
                <Route path="/:leagueSlug" element={<LeagueView />} />
                {/* Legacy auth routes - keep for admin/backwards compatibility */}
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/musicleague-transfer" element={<MusicLeagueTransfer />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                {/* Dashboard redirects to home */}
                <Route path="/dashboard" element={<Navigate to="/" replace />} />
                {/* Leagues page - public, filters by league from URL */}
                <Route path="/leagues" element={<Leagues />} />
                <Route
                  path="/leagues/create"
                  element={
                    <RequireAuth>
                      <CreateLeague />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/leagues/:id"
                  element={
                    <RequireAuth>
                      <LeagueDetail />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/leagues/:leagueId/rounds/:roundId/submit"
                  element={
                    <RequireAuth>
                      <SubmitSong />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/leagues/:leagueId/rounds/:roundId/vote"
                  element={
                    <RequireAuth>
                      <VoteRound />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/leagues/:leagueId/rounds/:roundId/results"
                  element={
                    <RequireAuth>
                      <RoundResults />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <RequireAuth>
                      <Profile />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <RequireAuth>
                      <Admin />
                    </RequireAuth>
                  }
                />
                {/* Playlists routes - now public, can filter by league */}
                <Route path="/playlists" element={<Playlists />} />
                <Route path="/playlists/player" element={<PlaylistPlayerPage />} />
                <Route path="/features" element={<Features />} />
                <Route path="/debug-db" element={<DbDebug />} />
                <Route
                  path="/seasons/create"
                  element={
                    <RequireAuth>
                      <CreateSeason />
                    </RequireAuth>
                  }
                />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              </TourProvider>
              </BrowserRouter>
              <Analytics />
          </TooltipProvider>
        </QueryClientProvider>
      </PlaybackProvider>
    </ThemeProvider>
  </StoreProvider>
);

export default App;
